import AccessTimeIcon from "@mui/icons-material/AccessTime"; // MUI 시간 아이콘

import {
  ScheduleComponent,
  Month,
  Inject,
  ViewsDirective,
  ViewDirective,
  PopupOpenEventArgs,
  EventRenderedArgs,
  NavigatingEventArgs,
  CellClickEventArgs,
} from "@syncfusion/ej2-react-schedule";
import dayjs from "dayjs";
import { memo, useCallback, useRef, useState } from "react";

import { useDeviceType } from "../../../hooks/useDeviceType";

import { Tooltip } from "@mui/material";
import {
  DateInfo,
  MeetingRoomStatusInfo,
  OutlookDeepLinkParam,
  RoomColorInfo,
} from "../../../system/types/meetingRoom/MeetingRoom";

import { isToday } from "../../../system/api/util/DateAndTimeUtil";
import { useMeetingRoom } from "../../../hooks/MeetingRoom/useMeetingRoom";
import MeetingRoomEventModal from "../Modal/Event/MeetingRoomEventModal";
import { useRecoilValue } from "recoil";
import { MyDataAtom } from "../../../system/atoms";

type MeetingRoomMonthSchedularProps = {
  dateInfo: DateInfo;
  selectedMeetingRoom: MeetingRoomStatusInfo | null;
  handleChangeDateInfo: (valueObject: Partial<DateInfo>) => void;
  colorList: RoomColorInfo[];
  meetingRoomScheduleList: any;
  handleClickOpenOutlookPopup: (params: OutlookDeepLinkParam[]) => void;
  getMeetingRoomEvents: () => void;
};

const MeetingRoomMonthSchedular = memo(
  ({
    dateInfo,
    selectedMeetingRoom,
    handleChangeDateInfo,
    colorList,
    meetingRoomScheduleList,
    handleClickOpenOutlookPopup,
    getMeetingRoomEvents,
  }: MeetingRoomMonthSchedularProps) => {
    const scheduleObj = useRef(null);
    const deviceType = useDeviceType();
    const {
      checkRoomInAttendee,
      getMeetingRoomBgColor,
      checkPersonInAttendee,
    } = useMeetingRoom();
    const { email } = useRecoilValue(MyDataAtom);
    // 팝업 이벤트 - 기존 popup cancel
    const handlePopupOpen = useCallback((args: PopupOpenEventArgs) => {
      args.cancel = true; // Cancel the default editor
    }, []);

    const [eventModalInfo, setEventModalInfo] = useState({
      isOpen: false,
      selectedEvent: {},
    });
    const handleIsOpen = useCallback((status: boolean) => {
      setEventModalInfo((prev) => ({
        ...prev,
        selectedEvent: status === false ? {} : prev.selectedEvent,
        isOpen: status,
      }));
    }, []);
    // 셀 클릭 이벤트
    const handleDoubleClickCell = useCallback(
      (args: CellClickEventArgs) => {
        // 셀의 날짜 정보
        const cellDate = args?.startTime
          ? dayjs(args.startTime).format("YYYY-MM-DD")
          : dayjs().format("YYYY-MM-DD");

        // 현재 시각 기준 (반올림: 10:10 → 10:30, 10:40 → 11:00)
        const now = dayjs();
        const rounded =
          now.minute() < 30
            ? now.startOf("hour").minute(30) // 0 ~ 29분 → 30분
            : now.startOf("hour").add(1, "hour"); // 30분 이상 → 정각

        // 셀 날짜에 반올림된 시간(HH:mm:ss)를 붙여 시작 시각 구성
        const start = dayjs(`${cellDate}T${rounded.format("HH:mm:ss")}`);
        const end = start.add(30, "minute"); // 종료 시간은 시작시간 + 30분

        // 아웃룩 팝업에 전달할 params
        const params = [
          { key: "startdt", value: start.format("YYYY-MM-DDTHH:mm:ss") },
          { key: "enddt", value: end.format("YYYY-MM-DDTHH:mm:ss") },
        ];

        // 회의실이 선택된 경우 cc정보 추가
        if (selectedMeetingRoom !== null) {
          const { roomInfo } = selectedMeetingRoom;
          params.push({
            key: "cc",
            value: `${roomInfo.displayName} <${roomInfo.mail}>`,
          });
        }
        handleClickOpenOutlookPopup(params);
      },
      [handleClickOpenOutlookPopup, selectedMeetingRoom]
    );

    // 커스텀 이벤트 템플릿
    const eventTemplate = useCallback(
      (props: any) => {
        const roomColor = getMeetingRoomBgColor(
          props.location,
          checkRoomInAttendee(props.attendees, selectedMeetingRoom?.roomInfo),
          selectedMeetingRoom,
          colorList
        );

        return (
          <Tooltip
            title={
              <div>
                <div>{props.Subject}</div>
                <div>
                  {`${dayjs(props.StartTime).format("HH:mm")} ~ ${dayjs(
                    props.EndTime
                  ).format("HH:mm")}`}
                </div>
                <div>{props.location}</div>
              </div>
            }
            placement="right"
          >
            <div
              style={{
                display: "flex",
                width: "100%",
                minWidth:
                  deviceType === "pc"
                    ? "134px"
                    : deviceType === "tablet"
                      ? "80px"
                      : "100%",
                justifyContent: "space-between",
                alignItems: "center",
                fontSize: `${deviceType === "mobile"
                  ? "10px !important"
                  : "12px !important"
                  }`,
                background: roomColor,
              }}
            >
              <div
                style={{
                  flex: 1,
                  maxWidth: deviceType === "pc" ? "86px" : "40px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  paddingLeft: "4px",
                  fontSize: "0.75rem",
                  fontWeight: 600,
                }}
              >
                {props.Subject}
              </div>
              {deviceType !== "mobile" && (
                <div
                  style={{
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    paddingRight: "4px",
                    fontSize: "0.75rem",
                    justifyContent: "flex-end",
                  }}
                >
                  <AccessTimeIcon
                    style={{ fontSize: "0.9rem", marginTop: "1px" }}
                  />
                  {`${dayjs(props.StartTime).format("HH:mm")}`}
                </div>
              )}
            </div>
          </Tooltip>
        );
      },
      [
        deviceType,
        colorList,
        checkRoomInAttendee,
        getMeetingRoomBgColor,
        selectedMeetingRoom,
      ]
    );

    // 오늘 날짜인 경우, 배경색 설정. 요일정보같은 부분은 제외.
    const onWorkCellRender = (args: any) => {
      const ignoredElementTypes = ["majorSlot", "minorSlot", "emptyCells"];
      if (
        !ignoredElementTypes.includes(args.elementType) &&
        isToday(args.date)
      ) {
        args.element.classList.add("custom-today-cell");
      }
    };

    // event의 style적용
    const applyEventStyle = useCallback(
      (args: EventRenderedArgs) => {
        // location 정보에서 roomColor 추출
        const location = args.data?.location;
        const attendees = args.data.attendees;

        const isAttendee = checkRoomInAttendee(
          attendees,
          selectedMeetingRoom?.roomInfo
        );

        const isPersonInAttendee = checkPersonInAttendee(attendees, email);

        const findBgColor = getMeetingRoomBgColor(
          location,
          isAttendee,
          selectedMeetingRoom,
          colorList
        );

        // location이 없거나 빈 문자열일 경우 기본 색상 적용
        args.element.style.backgroundColor = findBgColor;

        // 스타일 설정
        args.element.style.boxSizing = "border-box";
        args.element.style.border = "none";
        args.element.style.color = "inherit";
        args.element.style.cursor = "pointer";
        args.element.classList.add("custom-event");
        if (isPersonInAttendee) {
          args.element.classList.add("has-border");
          args.element.style.position = "relative";
          // args.element.style.borderLeft = "4px solid #B23448"; // Brand500
        }
      },
      [
        colorList,
        checkRoomInAttendee,
        getMeetingRoomBgColor,
        selectedMeetingRoom,
      ]
    );

    // event가 렌더될 때 applyEventStyle을 적용.
    const onEventRendered = useCallback(
      (args: EventRenderedArgs) => {
        if (scheduleObj.current) {
          applyEventStyle(args);
        }
      },
      [applyEventStyle]
    );

    // 날짜 변경 시 동작
    const handleViewNavigate = useCallback(
      (args: NavigatingEventArgs) => {
        const { currentDate, previousDate } = args;
        const current = dayjs(currentDate).format("YYYY-MM-DDTHH:mm");

        handleChangeDateInfo({
          stdDate: current,
        });
      },
      [handleChangeDateInfo]
    );

    const handleClickOpenEvent = useCallback((e) => {
      e.originalEvent.stopPropagation();
      // e.stopPropagation();
      // const isOrganizer = checkIsOrganizer(event, email);

      const event = e.event;

      setEventModalInfo({
        isOpen: true,
        selectedEvent: event,
      });
    }, []);

    return (
      <>
        <ScheduleComponent
          ref={scheduleObj}
          width="100%"
          height="100%"
          delayUpdate={true}
          currentView="Month"
          eventSettings={{
            dataSource: meetingRoomScheduleList,
            template: eventTemplate as any, // 상위 버전에서는 Function이 포함되지만, 하위버전에서는 stinrg | undefined라 Element로 작성된 eventTemplate의 타입을 any로 지정.
          }}
          eventClick={(e) => handleClickOpenEvent(e)}
          selectedDate={new Date(dateInfo.stdDate)}
          navigating={handleViewNavigate}
          popupOpen={handlePopupOpen}
          eventRendered={onEventRendered}
          locale="ko"
          rowAutoHeight={true}
          renderCell={onWorkCellRender}
          className="month-schedule-calendar"
          cellDoubleClick={handleDoubleClickCell}
        >
          <ViewsDirective>
            <ViewDirective option="Month" />
          </ViewsDirective>
          <Inject services={[Month]} />
        </ScheduleComponent>
        <MeetingRoomEventModal
          modalInfo={eventModalInfo}
          handleIsOpen={handleIsOpen}
          refetchEventList={getMeetingRoomEvents}
        />
      </>
    );
  }
);

export default MeetingRoomMonthSchedular;
